import { useGlobalProperties } from '@/src/composables/useGlobalProperties';
import { useWindow, type WindowSize } from '@/src/composables/useWindow';
import { theme } from '@/theme';
import { computed, onUnmounted, ref } from 'vue';

export type AdvisorsFilters = {
  status?: string;
  query?: string;
  sort?: string;
  order?: string;
  limit?: number;
  page?: number;
  isAuthenticated?: boolean;
  'categories[]'?: string;
};

export type Advisor = {
  id: number;
  name: string;
} & Record<string, any>;

export function useAdvisors() {
  const { getGlobalProperties } = useGlobalProperties();
  const { $api } = getGlobalProperties();
  const { windowWidth } = useWindow() as WindowSize;
  const cardName = theme.home.advisor?.cardName;

  const advisors = ref<Advisor[]>([]);
  const loading = ref(false);
  const page = ref(1);
  const total = ref(0);

  const perPage = computed(() => {
    if (windowWidth.value > 1440 && cardName === 'cover-image') return 8;
    return 6;
  });

  const nextPage = () => {
    page.value += 1;
  };

  const resetPage = () => {
    page.value = 1;
  };

  const setTotal = (newTotal: number) => {
    total.value = newTotal;
  };

  const fetchAdvisors = async ({ isAuthenticated = false, ...queryOpts }: AdvisorsFilters) => {
    if (loading.value) {
      return;
    }
    loading.value = true;

    const cleanQueryOpts = Object.fromEntries(Object.entries(queryOpts).filter(([_, value]) => value !== undefined));

    try {
      const { data }: any = await $api.get(`${isAuthenticated ? '/api' : ''}/user/advisors`, cleanQueryOpts);

      if (page.value > 1) {
        advisors.value = [...advisors.value, ...data.data];
      } else {
        advisors.value = data.data;
      }

      // Update total if it's available in the response
      if (data.total) {
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Error fetching advisors:', error);
    } finally {
      loading.value = false;
    }
  };

  onUnmounted(() => {
    advisors.value = [];
    loading.value = false;
    page.value = 1;
    total.value = 0;
  });

  return {
    // Data
    advisors,
    loading,
    page,
    total,
    perPage,

    // Methods
    fetchAdvisors,
    nextPage,
    resetPage,
    setTotal,
  };
}
